/**
 * ReasoningStep Compound Component
 *
 * Provides consistent layout structure for all reasoning steps.
 * Uses compound component pattern for flexibility.
 *
 * Usage:
 * <ReasoningStep ariaLabel="..." isLast={false}>
 *   <ReasoningStep.Icon stage="thinking" />
 *   <ReasoningStep.Content>
 *     <ReasoningStep.Header
 *       stage="thinking"
 *       status="completed"
 *       isExpanded={true}
 *       onToggle={() => {}}
 *     />
 *     <ReasoningStep.Body isExpanded={true}>
 *       ...content...
 *     </ReasoningStep.Body>
 *   </ReasoningStep.Content>
 * </ReasoningStep>
 */

import {
  ChevronDown,
  ClipboardCheck,
  Lightbulb04,
  MagicWand01,
  SearchLg,
} from '@untitledui/icons';
import type { ReactNode } from 'react';

import {
  getStageConfig,
  isStepInProgress,
  REASONING_DESIGN_TOKENS,
} from './design-tokens';
import { TimelineConnectorLine } from './TimelineConnectorLine';

/**
 * Custom Analysis Icon (AI sparkle variant)
 * Based on Figma design specification - AI Essentials Icon Set
 */
function AnalysisIcon({ className }: { className?: string }) {
  return (
    <svg
      width='27'
      height='27'
      viewBox='0 0 27 27'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
    >
      <path
        d='M11.5144 11.0872C11.2106 10.9297 10.9631 10.6878 10.8113 10.3841L10.7719 10.3053C10.62 10.0072 10.1981 10.0072 10.0463 10.3053L10.0069 10.3841C9.84938 10.6878 9.6075 10.9353 9.30375 11.0872L9.225 11.1266C8.92688 11.2784 8.92688 11.7003 9.225 11.8522L9.30375 11.8916C9.6075 12.0491 9.855 12.2909 10.0069 12.5947L10.0463 12.6734C10.1981 12.9716 10.62 12.9716 10.7719 12.6734L10.8113 12.5947C10.9688 12.2909 11.2106 12.0434 11.5144 11.8916L11.5931 11.8522C11.8913 11.7003 11.8913 11.2784 11.5931 11.1266L11.5144 11.0872Z'
        fill='currentColor'
      />
      <path
        d='M13.4775 13.0166L13.3425 13.0841C12.8081 13.3541 12.8081 14.1191 13.3425 14.3947L13.4775 14.4622C14.0231 14.7434 14.4675 15.1878 14.7487 15.7334L14.8162 15.8684C15.0863 16.4028 15.8512 16.4028 16.1269 15.8684L16.1944 15.7334C16.4756 15.1878 16.92 14.7434 17.4656 14.4622L17.6006 14.3947C18.135 14.1247 18.135 13.3597 17.6006 13.0841L17.4656 13.0166C16.92 12.7353 16.4756 12.2909 16.1944 11.7453L16.1269 11.6103C15.8569 11.0759 15.0919 11.0759 14.8162 11.6103L14.7487 11.7453C14.4675 12.2909 14.0231 12.7353 13.4775 13.0166Z'
        fill='currentColor'
      />
      <path
        d='M22.2638 5.61406L22.0837 5.52406C21.5212 5.23719 21.0713 4.78719 20.7844 4.22469L20.6944 4.04469C20.385 3.43719 19.7663 3.06031 19.0856 3.06031C18.405 3.06031 17.7863 3.43719 17.4712 4.04469L17.3812 4.22469C17.0944 4.78719 16.6444 5.23719 16.0819 5.52406L15.9019 5.61406C15.2944 5.92344 14.9175 6.54219 14.9175 7.22844C14.9175 7.91469 15.2944 8.52781 15.9019 8.84281L16.0819 8.93281C16.6444 9.21969 17.0944 9.66969 17.3812 10.2322L17.4712 10.4122C17.7806 11.0197 18.3994 11.3966 19.0856 11.3966C19.7719 11.3966 20.385 11.0197 20.7 10.4122L20.79 10.2322C21.0769 9.66969 21.5269 9.21969 22.0894 8.93281L22.2694 8.84281C22.8769 8.53344 23.2537 7.91469 23.2537 7.23406C23.2537 6.55344 22.8769 5.93469 22.2694 5.61969L22.2638 5.61406ZM21.4988 7.33531L21.3188 7.42531C20.4356 7.87531 19.7325 8.57844 19.2825 9.46156L19.1925 9.64156C19.1925 9.64156 19.1588 9.70906 19.0856 9.70906C19.0125 9.70906 18.9844 9.65844 18.9788 9.64156L18.8888 9.46156C18.4388 8.57844 17.7356 7.87531 16.8525 7.42531L16.6725 7.33531C16.6725 7.33531 16.605 7.30156 16.605 7.22844C16.605 7.15531 16.6556 7.12719 16.6725 7.12156L16.8525 7.03156C17.7356 6.58156 18.4388 5.87844 18.8888 4.99531L18.9788 4.81531C18.9788 4.81531 19.0125 4.74781 19.0856 4.74781C19.1588 4.74781 19.1869 4.79281 19.1925 4.81531L19.2825 4.99531C19.7325 5.87844 20.4356 6.58156 21.3188 7.03156L21.4988 7.12156C21.4988 7.12156 21.5662 7.15531 21.5662 7.22844C21.5662 7.30156 21.5156 7.32969 21.4988 7.33531Z'
        fill='currentColor'
      />
      <path
        d='M21.6562 10.6484C21.1894 10.6484 20.8125 11.0253 20.8125 11.4922C20.8125 15.5253 17.5331 18.8047 13.5 18.8047C9.46688 18.8047 6.1875 15.5253 6.1875 11.4922C6.1875 7.45906 9.46688 4.17969 13.5 4.17969C14.1862 4.17969 14.8669 4.27531 15.5194 4.46094C15.9694 4.59031 16.4363 4.33156 16.56 3.88156C16.6894 3.43156 16.4306 2.96469 15.9806 2.84094C15.1762 2.61031 14.3438 2.49219 13.5 2.49219C8.53875 2.49219 4.5 6.53094 4.5 11.4922C4.5 13.8547 5.4225 15.9978 6.91875 17.6066L5.95125 20.5034C5.92312 20.5878 5.90625 20.6778 5.90625 20.7678C5.90625 22.2641 8.61188 24.4241 13.5 24.4241C18.3881 24.4241 21.0938 22.2641 21.0938 20.7678C21.0938 20.6778 21.0769 20.5878 21.0487 20.5034L20.0813 17.6066C21.5775 15.9978 22.5 13.8547 22.5 11.4922C22.5 11.0253 22.1231 10.6484 21.6562 10.6484ZM7.63312 20.7959L8.29125 18.8159C8.94937 19.2828 9.66937 19.6653 10.44 19.9409L9.80438 22.1684C8.51625 21.7184 7.82437 21.1053 7.63312 20.7903V20.7959Z'
        fill='currentColor'
      />
    </svg>
  );
}

// ============================================================================
// Types
// ============================================================================

type StepStatus =
  | 'pending'
  | 'active'
  | 'processing'
  | 'completed'
  | 'waiting_human';

interface StageIconProps {
  stage: string;
  className?: string;
}

// ============================================================================
// Stage Icon Component
// ============================================================================

function StageIcon({ stage, className = '' }: StageIconProps) {
  const config = getStageConfig(stage);
  const iconClass = `w-5 h-5 ${REASONING_DESIGN_TOKENS.colors.iconColor} ${className}`;

  // Return appropriate icon based on stage
  switch (config.icon) {
    case 'lightbulb':
      return <Lightbulb04 className={iconClass} strokeWidth={1.5} />;
    case 'search':
      return <SearchLg className={iconClass} strokeWidth={1.5} />;
    case 'clipboard':
      return <ClipboardCheck className={iconClass} strokeWidth={1.5} />;
    case 'magic-wand':
      return <MagicWand01 className={iconClass} strokeWidth={1.5} />;
    case 'sparkle':
      return <AnalysisIcon className={iconClass} />;
    default:
      return <Lightbulb04 className={iconClass} strokeWidth={1.5} />;
  }
}

// ============================================================================
// Main Container
// ============================================================================

interface ReasoningStepProps {
  children: ReactNode;
  ariaLabel: string;
  isLast?: boolean;
  showConnector?: boolean;
}

function ReasoningStepRoot({
  children,
  ariaLabel,
  isLast = false,
  showConnector = true,
}: ReasoningStepProps) {
  return (
    <article
      className={`relative ${REASONING_DESIGN_TOKENS.spacing.stepPadding}`}
      aria-label={ariaLabel}
    >
      {/* Connector line - shown for all except last item */}
      {showConnector && !isLast && (
        <TimelineConnectorLine className='left-[12px] top-[34px] bottom-[-16px]' />
      )}
      <div className={REASONING_DESIGN_TOKENS.layout.stepContainer}>
        {children}
      </div>
    </article>
  );
}

// ============================================================================
// Icon Sub-component
// ============================================================================

interface ReasoningStepIconProps {
  stage: string;
}

function ReasoningStepIcon({ stage }: ReasoningStepIconProps) {
  return (
    <div
      className={`${REASONING_DESIGN_TOKENS.spacing.iconSize.main} flex items-center justify-center shrink-0`}
    >
      <StageIcon stage={stage} />
    </div>
  );
}

// ============================================================================
// Content Sub-component (wrapper for header + body)
// ============================================================================

interface ReasoningStepContentProps {
  children: ReactNode;
}

function ReasoningStepContent({ children }: ReasoningStepContentProps) {
  return (
    <div className={REASONING_DESIGN_TOKENS.layout.contentArea}>{children}</div>
  );
}

// ============================================================================
// Header Sub-component (title + status + chevron)
// ============================================================================

interface ReasoningStepHeaderProps {
  stage: string;
  status: StepStatus;
  isExpanded: boolean;
  onToggle: () => void;
}

function ReasoningStepHeader({
  stage,
  status,
  isExpanded,
  onToggle,
}: ReasoningStepHeaderProps) {
  const config = getStageConfig(stage);

  // Get status-specific text - only show for in-progress/waiting states
  const getStatusText = () => {
    if (isStepInProgress(status)) {
      return config.statusText;
    }
    if (status === 'waiting_human') {
      return 'Waiting for input';
    }
    // Don't show status text for completed steps
    return '';
  };

  return (
    <button
      onClick={onToggle}
      className={`w-full ${REASONING_DESIGN_TOKENS.layout.headerRow} cursor-pointer group rounded-lg transition-colors ${REASONING_DESIGN_TOKENS.states.hoverBg} -mx-2 px-2`}
    >
      <div className='flex items-center gap-2 min-w-0 h-6'>
        <span
          className={`${REASONING_DESIGN_TOKENS.typography.sectionTitle} ${REASONING_DESIGN_TOKENS.colors.sectionTitle}`}
        >
          {config.displayName}
        </span>
        {getStatusText() && (
          <span
            className={`${REASONING_DESIGN_TOKENS.typography.statusSubtitle} ${REASONING_DESIGN_TOKENS.colors.statusSubtitle} truncate relative top-[1px]`}
          >
            {getStatusText()}
          </span>
        )}
      </div>
      <ChevronDown
        className={`w-6 h-6 text-fg-primary shrink-0 transition-transform duration-200 ${
          isExpanded ? REASONING_DESIGN_TOKENS.states.chevronExpanded : ''
        }`}
        strokeWidth={1.5}
      />
    </button>
  );
}

// ============================================================================
// Body Sub-component (collapsible content)
// ============================================================================

interface ReasoningStepBodyProps {
  children: ReactNode;
  isExpanded: boolean;
}

function ReasoningStepBody({ children, isExpanded }: ReasoningStepBodyProps) {
  if (!isExpanded) {
    return null;
  }

  return (
    <div className='mt-2'>
      <div
        tabIndex={0}
        aria-label='Step content, scrollable'
        className={`${REASONING_DESIGN_TOKENS.typography.contentText} ${REASONING_DESIGN_TOKENS.colors.contentText} leading-[20px] overflow-x-auto`}
      >
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// SubItem Component (for nested items like property changes)
// ============================================================================

interface ReasoningStepSubItemProps {
  icon?: ReactNode;
  children: ReactNode;
  href?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

function ReasoningStepSubItem({
  icon,
  children,
  href,
  isFirst = false,
  isLast = false,
}: ReasoningStepSubItemProps) {
  const padding = isFirst ? 'pt-4 pb-1' : 'py-1';

  return (
    <div className='relative'>
      {/* Connector line for sub-items */}
      {!isLast && (
        <TimelineConnectorLine className='left-[8px] top-[28px] bottom-[-4px]' />
      )}
      <div className={`flex items-start gap-2 ${padding}`}>
        {icon && (
          <div
            className={`${REASONING_DESIGN_TOKENS.spacing.iconSize.sub} flex items-center justify-center shrink-0`}
          >
            {icon}
          </div>
        )}
        <div
          className={`${REASONING_DESIGN_TOKENS.typography.subItem} ${REASONING_DESIGN_TOKENS.colors.contentText} flex-1`}
        >
          {href ? (
            <a
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              className='text-fg-primary hover:underline'
            >
              {children}
            </a>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Compound Component Export
// ============================================================================

export const ReasoningStep = Object.assign(ReasoningStepRoot, {
  Icon: ReasoningStepIcon,
  Content: ReasoningStepContent,
  Header: ReasoningStepHeader,
  Body: ReasoningStepBody,
  SubItem: ReasoningStepSubItem,
});

export type { StageIconProps, StepStatus };
